import { MealPlanWithItems, MealPlanItem, Recipe } from '@/types/api';
import { DayServiceContainer } from './DayServiceContainer';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { getWeekDays } from '@/utils/dateUtils';
import { cn } from '@/lib/utils';

interface WeeklyCalendarProps {
  weekStart: Date;
  mealPlan?: MealPlanWithItems;
  onEditServings?: (item: MealPlanItem) => void;
  onRemoveItem?: (item: MealPlanItem) => void;
  onReplaceRecipe?: (item: MealPlanItem) => void;
  onSlotSelect?: (day: Date, service: 'comida' | 'cena') => void;
  onViewRecipe?: (recipe: Recipe) => void;
  onToggleFavorite?: (recipe: Recipe) => void;
  isFavorite?: (recipeId: string) => boolean;
  className?: string;
  disabled?: boolean;
}

export function WeeklyCalendar({
  weekStart,
  mealPlan,
  onEditServings,
  onRemoveItem,
  onReplaceRecipe,
  onSlotSelect,
  onViewRecipe,
  onToggleFavorite,
  isFavorite,
  className,
  disabled = false
}: WeeklyCalendarProps) {
  const weekDays = getWeekDays(weekStart);

  // Helper function to get meal plan item for specific day and service
  const getMealPlanItem = (day: Date, service: 'comida' | 'cena'): MealPlanItem | undefined => {
    if (!mealPlan?.items) return undefined;
    
    return mealPlan.items.find((item) => {
      const itemDate = new Date(item.day);
      const targetDate = new Date(day);
      
      return (
        itemDate.toDateString() === targetDate.toDateString() &&
        item.service === service
      );
    });
  };

  return (
    <Card className={cn("overflow-hidden", className)}>
      <CardContent className="p-4 sm:p-6">
        {/* Calendar por días */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {weekDays.map((day) => {
            const comidaItem = getMealPlanItem(day.date, 'comida');
            const cenaItem = getMealPlanItem(day.date, 'cena');
            
            return (
              <div key={day.date.toISOString()} className="space-y-3">
                {/* Day Header */}
                <div className="text-center pb-2 border-b border-gray-100">
                  <div className="flex items-center justify-center gap-2">
                    <div>
                      <div className="text-xs font-medium text-gray-600 uppercase">
                        {day.dayShort}
                      </div>
                      <div className={cn(
                        "text-lg font-semibold",
                        day.isToday 
                          ? "text-primary" 
                          : "text-gray-900"
                      )}>
                        {day.dayNumber}
                      </div>
                    </div>
                    {day.isToday && (
                      <Badge 
                        variant="default" 
                        className="text-xs px-2 py-0.5 h-auto"
                      >
                        Hoy
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Services for this day */}
                <div className="space-y-2">
                  {/* Comida */}
                  <DayServiceContainer
                    day={day.date}
                    service="comida"
                    mealPlanItem={comidaItem}
                    onEditServings={onEditServings}
                    onRemoveItem={onRemoveItem}
                    onReplaceRecipe={onReplaceRecipe}
                    onSlotSelect={onSlotSelect}
                    onViewRecipe={onViewRecipe}
                    onToggleFavorite={onToggleFavorite}
                    isFavorite={isFavorite}
                    disabled={disabled}
                  />

                  {/* Cena */}
                  <DayServiceContainer
                    day={day.date}
                    service="cena"
                    mealPlanItem={cenaItem}
                    onEditServings={onEditServings}
                    onRemoveItem={onRemoveItem}
                    onReplaceRecipe={onReplaceRecipe}
                    onSlotSelect={onSlotSelect}
                    onViewRecipe={onViewRecipe}
                    onToggleFavorite={onToggleFavorite}
                    isFavorite={isFavorite}
                    disabled={disabled}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
